import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { useToastContext } from '../../../contexts/ToastContext';
import { formatRelativeTime } from '../../../utils/dateUtils';
import { getHostname } from '../../../utils/leadPanelUtils';
import { fmtDateRange } from '../../../portal/pages/eventFormat';
import { Spinner } from '../../ui/Spinner';
import type { AdminSignup, AdminSignupApproval, SignupBriefStatus } from '../../../types/admin';

const PAGE_SIZE = 20;

const TH_CLASS = 'px-4 py-3 whitespace-nowrap text-left text-xs font-medium text-neutral-400 uppercase tracking-wide';

const BRIEF_PILL: Record<SignupBriefStatus, { label: string; className: string }> = {
  not_started: { label: 'Not started', className: 'bg-neutral-100 text-neutral-500' },
  drafting: { label: 'Drafting', className: 'bg-warning-50 text-warning-700' },
  ready: { label: 'Ready', className: 'bg-success-50 text-success-700' },
};

interface Outcome {
  userId: string;
  who: string;
  result: AdminSignupApproval;
}

const outcomeLines = (r: AdminSignupApproval): { text: string; warn: boolean }[] => {
  const email =
    r.email === 'sent'
      ? { text: 'Welcome email sent', warn: false }
      : r.email === 'skipped'
        ? { text: 'Welcome email skipped', warn: false }
        : { text: 'Welcome email failed: send it by hand', warn: true };
  const detail = r.slack.detail ? ` (${r.slack.detail})` : '';
  const slack = {
    invited: { text: 'Slack invite sent', warn: false },
    needs_setup: { text: `Slack needs setup: invite by hand${detail}`, warn: true },
    failed: { text: `Slack invite failed${detail}`, warn: true },
    skipped: { text: 'Slack skipped', warn: false },
  }[r.slack.status];
  return [email, slack];
};

const BriefPill: React.FC<{ status: SignupBriefStatus | null }> = ({ status }) => {
  const pill = BRIEF_PILL[status ?? 'not_started'];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${pill.className}`}>{pill.label}</span>;
};

const ConferenceCell: React.FC<{ project: AdminSignup['project'] }> = ({ project }) => {
  if (!project) return <span className="text-neutral-300">—</span>;
  const where = [project.city, fmtDateRange(project.date_start, project.date_end)].filter(Boolean).join(' · ');
  return (
    <>
      <div className="text-neutral-800 leading-tight">{project.event_name ?? project.name ?? 'Untitled'}</div>
      <div className="text-xs text-neutral-400 mt-0.5">{where}</div>
    </>
  );
};

const ApproveForm: React.FC<{
  signup: AdminSignup;
  onApproved: (result: AdminSignupApproval) => void;
  onCancel: () => void;
}> = ({ signup, onApproved, onCancel }) => {
  const { showToast } = useToastContext();
  const [name, setName] = useState(signup.company_name ?? '');
  const [pending, setPending] = useState(false);
  const inputId = `signup-workspace-name-${signup.user_id}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    try {
      const trimmed = name.trim();
      onApproved(await apiClient.approveAdminSignup(signup.user_id, trimmed ? { workspace_name: trimmed } : {}));
    } catch {
      showToast('Approval failed. Nothing was changed; try again.', 'error');
      setPending(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[220px] max-w-sm">
        <label htmlFor={inputId} className="block text-xs font-medium text-neutral-500 mb-1">
          Workspace name
        </label>
        <input
          id={inputId}
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={pending}
          className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-20"
        />
      </div>
      <button
        type="submit"
        id={`signup-approve-confirm-${signup.user_id}`}
        disabled={pending}
        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
      >
        {pending ? 'Approving…' : 'Approve and invite'}
      </button>
      <button
        type="button"
        id={`signup-approve-cancel-${signup.user_id}`}
        onClick={onCancel}
        disabled={pending}
        className="px-3 py-2 rounded-lg text-sm text-neutral-500 hover:text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-600"
      >
        Cancel
      </button>
    </form>
  );
};

const OutcomeList: React.FC<{ outcomes: Outcome[]; onDismiss: (userId: string) => void }> = ({ outcomes, onDismiss }) => (
  <ul className="space-y-2" aria-live="polite">
    {outcomes.map((o) => (
      <li key={o.userId} className="flex items-start gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 text-sm">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-neutral-900">Approved {o.who}</div>
          <ul className="mt-1 space-y-0.5">
            {outcomeLines(o.result).map((l) => (
              <li key={l.text} className={l.warn ? 'text-warning-700' : 'text-neutral-500'}>{l.text}</li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          onClick={() => onDismiss(o.userId)}
          className="text-neutral-400 hover:text-neutral-600"
          aria-label={`Dismiss outcome for ${o.who}`}
        >
          <X className="w-4 h-4" />
        </button>
      </li>
    ))}
  </ul>
);

/** Signups waiting for approval, paged server-side; approving provisions the workspace and invites. */
export const SignupsTab: React.FC = () => {
  const { showToast } = useToastContext();
  const [items, setItems] = useState<AdminSignup[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const reqIdRef = useRef(0);

  const fetchPage = useCallback(async (p: number) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const result = await apiClient.listAdminSignups(PAGE_SIZE, p * PAGE_SIZE);
      if (reqId !== reqIdRef.current) return;
      setItems(result.items);
      setTotal(result.total);
    } catch {
      if (reqId === reqIdRef.current) showToast('Failed to load signups', 'error');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { void fetchPage(page); }, [fetchPage, page]);

  const onApproved = (signup: AdminSignup, result: AdminSignupApproval) => {
    const who = signup.company_name ?? signup.name ?? signup.email ?? 'signup';
    setOutcomes((o) => [{ userId: signup.user_id, who, result }, ...o.filter((x) => x.userId !== signup.user_id)]);
    setApproving(null);
    const remaining = items.filter((s) => s.user_id !== signup.user_id);
    setItems(remaining);
    setTotal((t) => Math.max(0, t - 1));
    if (remaining.length === 0) {
      if (page > 0) setPage(page - 1);
      else void fetchPage(0);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-neutral-900">Signups</h2>
        <p className="text-sm text-neutral-500">People waiting for a workspace. Approving one creates it, emails them, and invites them to Slack.</p>
      </div>

      {outcomes.length > 0 && (
        <OutcomeList outcomes={outcomes} onDismiss={(id) => setOutcomes((o) => o.filter((x) => x.userId !== id))} />
      )}

      <div className="bg-white rounded-xl border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-100">
              <th className={TH_CLASS}>Person</th>
              <th className={TH_CLASS}>Company</th>
              <th className={TH_CLASS}>Role</th>
              <th className={TH_CLASS}>Conference</th>
              <th className={TH_CLASS}>Brief</th>
              <th className={TH_CLASS}>Signed up</th>
              <th className={TH_CLASS}><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-12"><Spinner size="md" text="Loading signups..." center /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-neutral-400">No signups waiting.</td></tr>
            ) : items.map((s) => {
              const host = s.company_website ? getHostname(s.company_website) : '';
              return (
                <React.Fragment key={s.user_id}>
                  <tr className="border-b border-neutral-50">
                    <td className="px-4 py-3.5">
                      <div className="font-medium text-neutral-900 leading-tight">{s.name ?? s.email ?? '—'}</div>
                      {s.name && <div className="text-xs text-neutral-400 mt-0.5">{s.email}</div>}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="text-neutral-800 leading-tight">{s.company_name ?? '—'}</div>
                      {host && (
                        <a href={`https://${host}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline">
                          {host}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-neutral-600">{s.role ?? '—'}</td>
                    <td className="px-4 py-3.5"><ConferenceCell project={s.project} /></td>
                    <td className="px-4 py-3.5"><BriefPill status={s.brief_status} /></td>
                    <td className="px-4 py-3.5 text-neutral-500 tabular-nums whitespace-nowrap" title={s.signed_up_at ?? undefined}>
                      {s.signed_up_at ? formatRelativeTime(s.signed_up_at) : '—'}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {approving !== s.user_id && (
                        <button
                          type="button"
                          id={`signup-approve-${s.user_id}`}
                          onClick={() => setApproving(s.user_id)}
                          aria-label={`Approve ${s.company_name ?? s.email ?? 'signup'}`}
                          className="px-3 py-1.5 rounded-lg border border-primary-600 text-primary-700 text-xs font-medium hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-600"
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                  {approving === s.user_id && (
                    <tr className="border-b border-neutral-100 bg-neutral-50/60">
                      <td colSpan={7} className="px-4 py-4">
                        <ApproveForm
                          signup={s}
                          onApproved={(r) => onApproved(s, r)}
                          onCancel={() => setApproving(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-between">
            <span className="text-xs text-neutral-400">
              {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length} of {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed text-neutral-500"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-neutral-500 px-2">{page + 1} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed text-neutral-500"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
